import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetTitle,
  Typography,
} from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import { Assignment, LearningOutcome } from "api/resources/assignments/types"
import TabBar from "components/TabBar"
import {
  Tab as TabBarTab,
  Tabs as TabBarTabs,
} from "components/TabBar/hooks/useTabs"
import FaqsContent from "features/Assignments/FAQs/Content"
import InstructionsContent from "features/Assignments/Instructions/Content"
import GuidelinesContent from "features/Assignments/SubmissionGuidelines/Content"
import { Xmark } from "iconoir-react"

import { buildAssignmentTitle } from "../../utils"
import LearningOutcomesContent from "../LearningOutcomes/Content"
import MarkingCriteriaContent from "../MarkingCriteriaSheet/Content"

export type DetailsTabId =
  | "guidelines"
  | "markingCriteria"
  | "instructions"
  | "learningOutcomes"
  | "faqs"

type Tab = {
  id: DetailsTabId
  label: string
  title: string
}

type Props = {
  activeTab: DetailsTabId | null
  onChangeTab: (tab: DetailsTabId) => void
  onClose: () => void
  assignment: Assignment
  courseId: number
  learningOutcomesContent?: LearningOutcome[]
}

const DetailsSheet = ({
  activeTab,
  onChangeTab,
  onClose,
  assignment,
  courseId,
  learningOutcomesContent,
}: Props) => {
  const isSmDown = useMediaQuery("only screen and (max-width : 599px)")

  const tabs: Tab[] = [
    assignment.submissionGuidelines && {
      id: "guidelines" as const,
      label: "Submission Guidelines",
      title: "Submission Guidelines",
    },
    {
      id: "markingCriteria" as const,
      label: "Marking Criteria",
      title: "Marking Criteria",
    },
    assignment.instructions && {
      id: "instructions" as const,
      label: "Task Instructions",
      title: "Task Instructions",
    },
    {
      id: "learningOutcomes" as const,
      label: "Learning Outcomes",
      title: "Assessment Criteria (AC) and Learning Outcomes (LO)",
    },
    {
      id: "faqs" as const,
      label: "FAQs",
      title: "Frequently Asked Questions (FAQs)",
    },
  ].filter((tab): tab is Tab => Boolean(tab))

  const activeTabData = tabs.find(tab => tab.id === activeTab)

  type DetailsTabBarTab = TabBarTab & { tabId: DetailsTabId }

  const tabBarTabs: TabBarTabs = tabs.map(
    (tab): DetailsTabBarTab => ({
      name: tab.label,
      content: () => null,
      tabId: tab.id,
    })
  )

  const activeTabBarTab =
    tabBarTabs.find(tab => (tab as DetailsTabBarTab).tabId === activeTab) ??
    tabBarTabs[0]

  const handleTabBarChange = (name: string) => {
    const match = tabBarTabs.find(tab => tab.name === name) as
      | DetailsTabBarTab
      | undefined
    if (match) onChangeTab(match.tabId)
  }

  return (
    <Sheet open={activeTab !== null} onOpenChange={onClose}>
      <SheetContent
        side={isSmDown ? "bottom" : "right"}
        height={isSmDown ? 95 : 100}
        closeWhenInteractOutside
        className="w-full !max-w-3xl !rounded-t-2xl py-2.5 sm:!rounded-none"
      >
        <SheetBody className="!px-0 !pb-3">
          <Button
            className="mx-2 px-1 text-onSurface-900"
            variant={"text"}
            onClick={onClose}
            startAdornment={<Xmark className="!size-2.5 text-onSurface-900" />}
          >
            Close
          </Button>

          <SheetTitle className="mx-3 mb-3 mt-2.5">
            <Typography variant="title2">
              {buildAssignmentTitle(assignment)}
            </Typography>
          </SheetTitle>

          <TabBar
            tabs={tabBarTabs}
            activeTab={activeTabBarTab}
            onChange={handleTabBarChange}
            className="border-b border-onSurface-200 px-2.5 sm:px-4"
          />

          <div className="px-2.5 sm:px-4">
            <Typography variant="title3" className="my-2.5">
              {activeTabData?.title}
            </Typography>

            {activeTab === "guidelines" && assignment.submissionGuidelines && (
              <GuidelinesContent guidelines={assignment.submissionGuidelines} />
            )}

            {activeTab === "markingCriteria" && (
              <MarkingCriteriaContent assignmentId={assignment.uuid} enabled />
            )}

            {activeTab === "instructions" && assignment.instructions && (
              <InstructionsContent instructions={assignment.instructions} />
            )}

            {activeTab === "learningOutcomes" && (
              <LearningOutcomesContent
                acs={assignment.assignmentAssessmentCriteria}
                learningOutcomesContent={learningOutcomesContent}
                pedagogicalSkillEvaluated={assignment.pedagogicalSkillEvaluated}
              />
            )}

            {activeTab === "faqs" && (
              <FaqsContent data={assignment} courseId={courseId} />
            )}
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

export default DetailsSheet
